import { queryClient } from "./queryClient"
import {
  PrerequisiteFailedError,
  isPrerequisiteFailedError,
} from "./PrerequisiteFailedError"
import { ENTITY_CREATE_MUTATION_KEYS } from "@/lib/queryClient/mutationKeys"
import { MutationMeta } from "@tanstack/react-query"

/**
 * Check if a mutation key matches one of the gating keys.
 */
function isGatingMutationKey(key: readonly unknown[] | undefined): boolean {
  if (!key) return false
  return ENTITY_CREATE_MUTATION_KEYS.some(
    (gatingKey) =>
      gatingKey.length === key.length &&
      gatingKey.every((part, i) => part === key[i]),
  )
}

/**
 * Wraps a mutationFn to check for failed prerequisites in the same scope
 * before executing. If a gating mutation (CREATE) in the same scope has
 * status === "error" (and it's not a PrerequisiteFailedError), the wrapped
 * function throws PrerequisiteFailedError instead of making a network request.
 *
 * When the prerequisite is later retried and succeeds, the scopeCascadeRetry
 * subscriber will automatically re-execute the blocked mutations.
 *
 * @param mutationFn - The original mutation function to wrap
 * @param meta - Optional metadata to identify the mutation for scope lookup
 * @returns A wrapped mutation function with scope gate checking
 */
export function withScopeGate<TPayload, TResult>(
  mutationFn: (payload: TPayload) => Promise<TResult>,
): (payload: TPayload, meta?: MutationMeta) => Promise<TResult> {
  return async (payload: TPayload) => {
    // Find the current mutation in the cache by matching variables reference
    const allMutations = queryClient.getMutationCache().getAll()
    const currentMutation = allMutations.find(
      (m) => m.state.variables === payload,
    )

    if (!currentMutation) {
      // Can't find ourselves in the cache — just proceed
      return mutationFn(payload)
    }

    // Read scope from the mutation's options
    const scopeId = currentMutation.options.scope?.id
    if (!scopeId) {
      // No scope — no gating needed
      return mutationFn(payload)
    }

    // Check if any gating mutation in the same scope has failed
    const gatingMutations = allMutations.filter((m) => {
      if (m === currentMutation) return false
      if (m.options.scope?.id !== scopeId) return false
      if (m.state.status !== "error") return false
      if (!isGatingMutationKey(m.options.mutationKey)) return false
      // Don't be blocked by other PrerequisiteFailedErrors (only real failures)
      if (isPrerequisiteFailedError(m.state.error)) return false
      return true
    })

    if (gatingMutations.length > 0) {
      throw new PrerequisiteFailedError(scopeId)
    }

    return mutationFn(payload)
  }
}
